import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTranscriptVersions } from "../../../shared/api/transcriptsApi";
import { TranscriptVersionSummary } from "../../../shared/interfaces/Transcript";
import {
  transcriptsCjiCacheOptions,
  transcriptsErrorMessage,
  transcriptsKeys,
} from "./transcriptsQueries";

interface UseTranscriptVersionsResult {
  versions: TranscriptVersionSummary[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Stable empty result so `versions` doesn't churn identity on every render
// while the query has no data (the old hook's initial state was one stable []).
const NO_VERSIONS: TranscriptVersionSummary[] = [];

/**
 * Loads the transcript's version history (for the History picker) on
 * react-query, same return shape as the hand-rolled version. `refetch` is how
 * the editor refreshes history after a save appends a new head version.
 */
export const useTranscriptVersions = (
  evidenceId: string | undefined,
): UseTranscriptVersionsResult => {
  const query = useQuery({
    queryKey: transcriptsKeys.versions(evidenceId),
    queryFn: ({ signal }) => {
      if (!evidenceId) throw new Error("evidenceId is required");
      return getTranscriptVersions(evidenceId, signal);
    },
    enabled: !!evidenceId,
    ...transcriptsCjiCacheOptions,
  });

  const { refetch: queryRefetch } = query;
  const refetch = useCallback(() => {
    // Preserve the old hook's no-op without an evidenceId — v5's refetch()
    // would fetch even a disabled query.
    if (evidenceId) void queryRefetch();
  }, [evidenceId, queryRefetch]);

  return {
    versions: query.data ?? NO_VERSIONS,
    // isFetching, not isPending: refetch-after-save counts as loading, and the
    // flag stays false while disabled.
    isLoading: query.isFetching,
    error: query.error
      ? transcriptsErrorMessage(query.error, "Failed to load version history")
      : null,
    refetch,
  };
};
